import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import Icon from '../Icon';
import style from './SearchBar.module.less';
import clsx from 'clsx';
import dynamic from 'next/dynamic';

const AutoSuggestWrapper = dynamic(() => import('./AutoSuggestWrapper'), {
  ssr: false,
});

/**
 * @typedef SearchSuggestion
 * @property {string} term The value to use as a search term
 * @property {string} display The human-friendly display value
 */

function SearchBarV1({
  placeholder = 'Search entire library',
  onSubmit = () => {},
  className,
  page,
  searchMetaTitle,
  query,
}) {
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [value, setValue] = useState('');
  const [searchWithin, setSearchWithin] = useState(false);
  const [searchAutoSuggest, setSearchAutoSuggest] = useState(false);
  const rootRef = useRef(null);
  // make connection between search query and search bar value
  useEffect(() => {
    if (page !== 'search') {
      setValue('');
      return;
    }
    setValue(query);
  }, [query]);

  // Retrieve complete suggestion list from the PHP api.
  useEffect(() => {
    const asyncFunc = async () => {
      const res = await fetch(
        `https://${window.location.hostname}/api/auto-complete/data-list/all`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const responseJSON = await res.json();

      setSearchSuggestions(responseJSON);
    };

    asyncFunc().catch();
  }, []);

  /**
   * Submit a request to search
   *
   * @param {string} suggestion the suggestion itself
   * @param {boolean} directSearch True if this is a direct search, false otherwise.
   */
  const doSubmit = (suggestion, directSearch = false) => {
    const {
      term,
      display,
      searchValue,
      suggestionType,
      termMatchSuggestion,
    } = suggestion;

    Promise.resolve(
      onSubmit(
        term,
        suggestionType === 'searchWithin',
        directSearch,
        termMatchSuggestion
      )
    ).then(() => {
      if (suggestionType === 'searchWithin') {
        setValue(searchValue);
      } else if (suggestionType === 'searchAll') {
        setValue(term);
      } else {
        setValue(display);
      }
      setSearchWithin(false);
      setSearchAutoSuggest(false);
    });
  };

  /**
   * Handle a change in the value of the search input.
   *
   * @param {Event} event
   * @param {Object} args
   * @param {string} args.newValue The new input value
   */
  const onChange = (event, { newValue }) => {
    event.preventDefault();
    setValue(newValue);
  };

  /**
   * Handle the submission of the encompassing form.
   * This should only handle submissions that are direct search
   *
   * @param {Event} event
   */
  const handleSubmit = event => {
    event.preventDefault();
    // Filter out searches that are not direct search, this is only when user select suggestions
    // using 'Enter' key and we want to prevent a second search
    if (searchWithin || searchAutoSuggest) {
      return;
    }

    var matches = searchSuggestions.filter(function(sugg) {
      return sugg.display === value;
    });

    doSubmit(
      {
        term: matches.length >= 1 ? matches[0].term : value,
        display: value,
        suggestionType: undefined,
        termMatchSuggestion: matches.length >= 1,
      },
      true
    );
  };

  /**
   * Handle the request to clear the search term.
   */
  const handleClear = () => {
    setValue('');
  };

  return (
    <div className={clsx(style.container, className)} ref={rootRef}>
      <div className={style['search-bar-container']}>
        <div className={style['search-bar']}>
          <div className={clsx(style['search-form'], 'form')}>
            <div className={clsx(style['search-box'])}>
              <form
                action="/resources/"
                method="get"
                title="Search 38,000+ Educational Resources"
                onSubmit={handleSubmit}
                role="search"
              >
                <AutoSuggestWrapper
                  doSubmit={doSubmit}
                  value={value}
                  searchInputClass={style['search-input']}
                  placeholder={placeholder}
                  onChange={onChange}
                  page={page}
                  searchMetaTitle={searchMetaTitle}
                  query={query}
                  setSearchWithin={setSearchWithin}
                  setSearchAutoSuggest={setSearchAutoSuggest}
                  searchSuggestions={searchSuggestions}
                />
                <Button
                  title="Search 38,000+ Educational Resources"
                  type="submit"
                  className={clsx(style['search-button'], style.btn)}
                  color="tertiary"
                  shadowless={true}
                >
                  <Icon name="search" isImg={false} />
                </Button>
                {value.length > 0 ? (
                  <Button
                    title="Clear"
                    className={clsx(style['search-button-clear'], style.btn)}
                    color="tertiary"
                    shadowless={true}
                    onClick={handleClear}
                  >
                    <Icon
                      name="cancel-circled"
                      className={style['cancel-button']}
                      isImg={false}
                    />
                  </Button>
                ) : null}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SearchBarV1.propTypes = {
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func,
  className: PropTypes.string,
  page: PropTypes.string,
  searchMetaTitle: PropTypes.string,
  query: PropTypes.string,
};

export default SearchBarV1;
